import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useSolPrice } from "@/hooks/useSolPrice";
import { toast } from "sonner";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionError";
import {
  Star,
  Loader2,
  RefreshCw,
  Gift,
  Crown,
  AlertCircle,
  Sparkles,
  Copy,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const ADMIN_WALLET = "ADJkonbgxndjbyL5XHfBLkVhEGiTUBChK9YMg8PtmPKa";
export const BUY_STARS_REOPEN_KEY = "flow_buystars_reopen";

const PACKAGES = [
  { usd: 5,  stars: 10, label: "10 Stars",       bonus: "+100%", icon: Star,     color: "from-cyan-500 to-blue-500",     borderColor: "border-cyan-500/30",  doubleBalance: false },
  { usd: 10, stars: 15, label: "15 Stars",       bonus: "+50%",  icon: Gift,     color: "from-yellow-500 to-orange-500", borderColor: "border-yellow-500/30",doubleBalance: false },
  { usd: 15, stars: 50, label: "50 Stars",       bonus: "+233%", icon: Crown,    color: "from-purple-500 to-pink-500",   borderColor: "border-purple-500/30",doubleBalance: false },
  { usd: 20, stars: 0,  label: "Double Balance", bonus: "x2",    icon: Sparkles, color: "from-pink-500 to-fuchsia-500",  borderColor: "border-pink-500/40",  doubleBalance: true },
];

interface BuyStarsModalProps {
  open: boolean;
  onClose: () => void;
  playerId: string;
  currentBalance: number;
  onSuccess: () => void;
}

export function BuyStarsModal({ open, onClose, playerId, currentBalance, onSuccess }: BuyStarsModalProps) {
  const [selectedPkg, setSelectedPkg] = useState(1);
  const [step, setStep] = useState<"choose" | "pay">("choose");
  const [lockedQuote, setLockedQuote] = useState<null | { solPrice: number; solAmount: number; packageUsd: number; capturedAt: number }>(null);
  const [txSignature, setTxSignature] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollDeadlineRef = useRef<number>(0);
  const { price: solPrice, isLoading: priceLoading, refresh: refreshPrice, error: priceError } = useSolPrice();

  const pkg = PACKAGES[selectedPkg];
  const liveSolNeeded = pkg.usd > 0 && solPrice > 0 ? pkg.usd / solPrice : 0;
  const quotedSolNeeded = lockedQuote?.solAmount ?? liveSolNeeded;
  const solDisplay = quotedSolNeeded > 0 ? quotedSolNeeded.toFixed(6) : "—";

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setStep("choose");
      setLockedQuote(null);
      setTxSignature("");
      setShowManualFallback(false);
      setDetecting(false);
    }
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [open]);

  const copy = async (text: string, which: "wallet" | "amount") => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "wallet") {
        setCopiedWallet(true);
        setTimeout(() => setCopiedWallet(false), 1500);
      } else {
        setCopiedAmount(true);
        setTimeout(() => setCopiedAmount(false), 1500);
      }
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleProceed = () => {
    if (liveSolNeeded <= 0) {
      toast.error("SOL price unavailable. Refresh and try again.");
      return;
    }
    setLockedQuote({
      solPrice,
      solAmount: liveSolNeeded,
      packageUsd: pkg.usd,
      capturedAt: Date.now(),
    });
    setStep("pay");
  };

  const handleVerify = async () => {
    const sig = txSignature.trim();
    if (!sig || sig.length < 40) {
      toast.error("Paste a valid transaction signature");
      return;
    }
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("recover-star-purchase", {
        body: {
          tx_signature: sig,
          player_id: playerId,
          package_usd: lockedQuote?.packageUsd ?? pkg.usd,
          quoted_sol_price: lockedQuote?.solPrice ?? solPrice,
        },
      });
      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error, "Verification failed"));
      }
      const res = data as any;
      if (res?.success) {
        toast.success(
          res.already_credited
            ? "Purchase already credited ✅"
            : `+${res.stars_credited} Stars credited ✅ New balance: $${Number(res.new_balance).toFixed(2)}`,
        );
        onSuccess();
        onClose();
      } else {
        toast.error(res?.error || "Verification failed");
      }
    } catch (e: any) {
      toast.error(e?.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setDetecting(false);
  };

  const pollOnce = async (sinceTs: number) => {
    try {
      const { data, error } = await supabase.functions.invoke("auto-detect-star-payment", {
        body: {
          player_id: playerId,
          package_usd: lockedQuote?.packageUsd ?? pkg.usd,
          quoted_sol_price: lockedQuote?.solPrice ?? solPrice,
          since_ts: sinceTs,
        },
      });
      if (!error) {
        const res = data as any;
        if (res?.status === "credited") {
          stopPolling();
          toast.success(
            res.already_credited
              ? "Purchase already credited ✅"
              : `+${res.stars_credited} Stars credited ✅ New balance: $${Number(res.new_balance).toFixed(2)}`,
          );
          onSuccess();
          onClose();
          return;
        }
      }
    } catch (e) {
      console.warn("auto-detect exception:", e);
    }

    if (Date.now() < pollDeadlineRef.current) {
      pollTimerRef.current = window.setTimeout(() => pollOnce(sinceTs), POLL_INTERVAL_MS);
    } else {
      stopPolling();
      setShowManualFallback(true);
      toast.error("We couldn't detect your payment automatically. Please paste your transaction signature below.");
    }
  };

  const handleIvePaid = () => {
    if (detecting) return;
    const startedAt = Math.floor(Date.now() / 1000);
    setDetecting(true);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollTimerRef.current = window.setTimeout(() => pollOnce(startedAt), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !verifying && onClose()}>
      <DialogContent className="max-w-sm bg-gray-900 border border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Star className="w-5 h-5 text-yellow-400" />
            Reload Credit
          </DialogTitle>
          <DialogDescription className="text-gray-300">
            {step === "choose"
              ? "Buy Stars with SOL. The more you spend, the bigger the bonus!"
              : "Send SOL from any wallet, then tap \"I've paid\" — we'll detect it automatically."}
          </DialogDescription>
        </DialogHeader>

        {step === "choose" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {PACKAGES.map((p, i) => {
                const Icon = p.icon;
                const isSelected = selectedPkg === i;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedPkg(i)}
                    className={`relative rounded-xl p-3 border-2 transition-all text-center ${
                      isSelected
                        ? `${p.borderColor} bg-white/10 scale-105 shadow-lg`
                        : "border-white/10 bg-black/40 hover:bg-white/5"
                    }`}
                  >
                    <span className={`absolute -top-2 -right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r ${p.color} text-white`}>
                      {p.bonus}
                    </span>
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? "text-yellow-400" : "text-gray-500"}`} />
                    <div className="text-lg font-extrabold text-white">${p.usd}</div>
                    <div className="text-[11px] text-yellow-400 font-bold leading-tight">{p.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="bg-black/60 rounded-lg p-3 border border-white/5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">SOL price:</span>
                <span className="text-white font-semibold flex items-center gap-1">
                  {priceLoading ? <Loader2 className="w-3 h-3 animate-spin" />
                    : priceError ? <span className="text-red-400">Error</span>
                    : `$${solPrice.toFixed(2)}`}
                  <button onClick={refreshPrice} className="text-gray-500 hover:text-white"><RefreshCw className="w-3 h-3" /></button>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">You pay:</span>
                <span className="text-cyan-400 font-bold">{solDisplay} SOL</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">You receive:</span>
                <span className="text-yellow-400 font-bold flex items-center gap-1">
                  <Star className="w-3.5 h-3.5" />
                  {pkg.doubleBalance
                    ? `+$${currentBalance.toFixed(2)} (doubled)`
                    : `${pkg.stars} Stars ($${pkg.stars}.00 credit)`}
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} className="border-white/20 text-gray-300 hover:bg-white/10">
                Cancel
              </Button>
              <Button
                onClick={handleProceed}
                disabled={liveSolNeeded <= 0 || priceLoading || !!priceError || (pkg.doubleBalance && currentBalance <= 0)}
                className={`font-bold bg-gradient-to-r ${pkg.color} text-white hover:opacity-90 border-0`}
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "pay" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-100 flex gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <span>
                Open your Solana wallet (Phantom, Solflare, Backpack, exchange, etc.) and send the EXACT
                amount of SOL to the address below. Then tap "I've paid".
              </span>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wide text-gray-400">Send to (treasury wallet)</label>
              <div className="flex items-center gap-2 bg-black/60 rounded-lg p-2 border border-white/10">
                <code className="flex-1 text-[11px] text-cyan-300 break-all">{ADMIN_WALLET}</code>
                <button
                  onClick={() => copy(ADMIN_WALLET, "wallet")}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Copy wallet"
                >
                  {copiedWallet ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wide text-gray-400">Amount to send</label>
              <div className="flex items-center gap-2 bg-black/60 rounded-lg p-2 border border-white/10">
                <code className="flex-1 text-sm text-yellow-300 font-bold">{solDisplay} SOL</code>
                <span className="text-[11px] text-gray-500">(${pkg.usd})</span>
                <button
                  onClick={() => copy(solDisplay, "amount")}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Copy amount"
                >
                  {copiedAmount ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-500">
                Tolerance ±0.005 SOL or ±$1. Sending the wrong amount may not match a package.
              </p>
              {lockedQuote && (
                <p className="text-[10px] text-cyan-400">
                  Rate locked at ${lockedQuote.solPrice.toFixed(2)} per SOL for this payment.
                </p>
              )}
            </div>

            {detecting && (
              <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-3 text-xs text-white flex gap-2">
                <Loader2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0 animate-spin" />
                <div className="space-y-1">
                  <p className="font-semibold">Payment detection in progress…</p>
                  <p className="text-gray-300">
                    This may take a few seconds. Your Stars will appear in your balance shortly — please don't close this window.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setStep("choose");
                  setLockedQuote(null);
                }}
                disabled={verifying || detecting}
                className="border-white/20 text-gray-300 hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                onClick={handleIvePaid}
                disabled={detecting || verifying}
                className={`font-bold bg-gradient-to-r ${pkg.color} text-white hover:opacity-90 border-0`}
              >
                {detecting ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Detecting…</>
                ) : (
                  <><Star className="w-4 h-4 mr-1" /> I've paid</>
                )}
              </Button>
            </DialogFooter>

            {/* Emergency manual fallback — only for failed payments */}
            <div className="mt-2 pt-3 border-t border-dashed border-white/10 opacity-70">
              <button
                type="button"
                onClick={() => setShowManualFallback((v) => !v)}
                className="w-full flex items-center justify-between text-[10px] text-gray-400 hover:text-white"
              >
                <span>⚠ Payment failed or not detected? Recover with TX signature</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showManualFallback ? "rotate-180" : ""}`} />
              </button>
              {showManualFallback && (
                <div className="space-y-2 mt-3">
                  <p className="text-[10px] text-gray-500">
                    Use this ONLY if your payment was sent but auto-detection failed. You don't need this for a normal purchase.
                  </p>
                  <Input
                    value={txSignature}
                    onChange={(e) => setTxSignature(e.target.value)}
                    placeholder="Paste tx signature from your wallet"
                    className="bg-black/60 border-white/10 text-white text-xs"
                    disabled={verifying}
                  />
                  <Button
                    onClick={handleVerify}
                    disabled={verifying || !txSignature.trim()}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    {verifying ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Verifying...</>
                    ) : (
                      <>Recover payment manually</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
